// hook customizado para obter usuário autenticado.
import { useAuth } from './auth';

/**
 * Tipo que representa as permissões exigidas para acessar um recurso.
 *
 * O sistema aceita:
 * - Uma única permissão (string)
 * - Ou múltiplas permissões (array de strings)
 *
 * Exemplo:
 * "dashboard"
 * ["dashboard", "reports"]
 */
type Permissions = Array<string> | string;

/**
 * Tipo da função que será retornada pelo hook.
 *
 * Essa função recebe permissões necessárias
 * e retorna um boolean indicando se o usuário possui acesso.
 */
type Response = (permissions: Permissions) => boolean;

/**
 * Hook customizado responsável por validar se o usuário
 * possui permissão para acessar determinada funcionalidade
 * ou página do sistema.
 *
 * Esse hook atua como uma camada de autorização
 * baseada nas permissões associadas ao usuário autenticado.
 *
 * Funcionamento:
 * 1. Obtém o usuário logado através do hook de autenticação.
 * 2. Recupera as permissões desse usuário.
 * 3. Retorna uma função que poderá ser usada nos componentes
 *    para verificar acesso a recursos específicos.
 *
 * Exemplo de uso:
 *
 * const can = useCan();
 *
 * if (can("dashboard")) {
 *   // usuário pode acessar
 * }
 */
const useCan = (): Response => {

  // Obtendo o usuário do hook customizado
  const { user } = useAuth();

  const userPermissions = user?.permissions;  

  /**
   * Este hook retorna uma função responsável por validar permissões do usuário.
   * Ou seja, ao utilizar o hook, recebemos uma função que pode ser chamada em qualquer
   * parte do componente para verificar se o usuário tem acesso a determinado recurso.
   */
  return function can(permissions) {
      
    // Caso o usuário não possua permissões carregadas
    // significa que ele não pode acessar o recurso
    if (!userPermissions) {
      return false;
    }
  
    /**
     * Normalização da entrada:
     * Se vier uma única permissão (string),
     * transformamos em array para padronizar o processamento.
     */
    const permissionsArray = Array.isArray(permissions)
      ? permissions
      : [permissions];
    /**
     * Aqui ocorre a verificação das permissões.
     * O método every percorre todas as permissões exigidas
     * e valida se o usuário possui cada uma delas.
     */
    const hasPermissions = permissionsArray.every((permission) =>
      userPermissions.includes(permission),
    );

    // Retorna se o usuário possui todas as permissões necessárias
    return hasPermissions;
  };
};

export default useCan;
